// Helpers for building, reading and detecting calculator share links.

// Query keys that can appear on a page without meaning "restore a shared
// calculation": embed mode, campaign tags, click IDs.
const IGNORED_KEYS = /^(embed|utm_[a-z]+|fbclid|gclid|ref)$/i;

// Keep share links readable: spaces become "+", and ":" / "," stay literal
// (both are legal in a query string and URLSearchParams decodes them the
// same either way). "+" itself is escaped by encodeURIComponent, so it can't
// be confused with a space.
const encodeValue = (value) =>
  encodeURIComponent(String(value))
    .replace(/%20/g, '+')
    .replace(/%3A/gi, ':')
    .replace(/%2C/gi, ',')
    .replace(/%2F/gi, '/');

/**
 * Build a share URL for the current page from a flat key/value map. Keys
 * whose value is null, undefined or '' are omitted. Existing query params
 * (e.g. ?embed=1) are intentionally dropped so a share link never carries
 * the sharer's session flags.
 */
export const buildShareUrl = (params, pathname = window.location.pathname) => {
  const query = Object.entries(params)
    .filter(([, value]) => value !== undefined && value !== null && value !== '')
    .map(([key, value]) => `${key}=${encodeValue(value)}`)
    .join('&');
  return `${window.location.origin}${pathname}${query ? `?${query}` : ''}`;
};

/**
 * Reader over the query string. `get('c', 'camera')` returns the first key
 * present (so legacy long-form keys keep working), or null when none is.
 * `get.has(...keys)` tells whether any of the keys is present.
 */
export const readShareParams = (search = window.location.search) => {
  const params = new URLSearchParams(search);
  const get = (...keys) => {
    const key = keys.find((k) => params.has(k));
    return key === undefined ? null : params.get(key);
  };
  get.has = (...keys) => keys.some((k) => params.has(k));
  return get;
};

/** True when the query string carries calculator state to restore. */
export const hasShareParams = (search = window.location.search) =>
  [...new URLSearchParams(search).keys()].some((key) => !IGNORED_KEYS.test(key));

// ---- shared value validators ------------------------------------------------

// Some database names contain doubled spaces ("ALEXA  35"); compare and emit
// them with whitespace collapsed so links stay tidy and still match.
export const squash = (value) => String(value ?? '').replace(/\s+/g, ' ').trim();

/** The entry of `list` whose name matches `name` (whitespace-insensitive). */
export const findName = (list, name) =>
  name == null ? undefined : list.find((item) => squash(item) === squash(name));

const TIMECODE = /^\d{1,3}:\d{2}:\d{2}:\d{2}$/;
export const timecodeOr = (token, fallback) => (TIMECODE.test(token || '') ? token : fallback);

/** Positive number as the string the calculators store ("23.976", "24"). */
export const positiveNumberOr = (token, fallback) => {
  const value = parseFloat(token);
  return Number.isFinite(value) && value > 0 ? String(value) : fallback;
};

export const positiveIntOr = (token, fallback) => {
  const value = parseInt(token, 10);
  return Number.isFinite(value) && value >= 1 ? value : fallback;
};

/** "1.915 TB" -> { text: "1.915 TB", size: "1.915", unit: "TB" }, or null. */
export const parseSizeToken = (token) => {
  const match = String(token ?? '').trim().match(/^(\d+(?:\.\d+)?)\s*(B|KB|MB|GB|TB|PB)$/i);
  if (!match) return null;
  const unit = match[2].toUpperCase();
  return { size: match[1], unit, text: `${match[1]} ${unit}` };
};
